import { useMemo } from 'react';
import { Box, MenuItem, Typography } from '@mui/material';
import {
  CustomTextArea,
  CustomTextField,
  CustomSelect,
  CustomAutoComplete,
  HorizontalForm,
  VerticalForm,
} from '../../../common';
import InternalOrderMaterialsSection from './InternalOrderMaterialsSection';
import { getProjectOptionLabel, sortProjectOptions } from '../utils/internalOrdersUtils';

const InternalOrderFormSection = ({
  selectedOrder,
  onInputChange,
  onSelectChange,
  isMobile,
  warehouses = [],
  warehousesLoading = false,
  projects = [],
  projectsLoading = false,
  searchEnabled = true,
  onAddMaterial,
  onUpdateMaterialQuantity,
  onRemoveMaterial,
}) => {
  const projectOptions = useMemo(
    () =>
      sortProjectOptions(projects).map((project) => ({
        value: project.uuid,
        label: getProjectOptionLabel(project) || 'Proyecto sin nombre',
      })),
    [projects]
  );

  const sectionTitle = (
    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
      Datos del pedido
    </Typography>
  );

  const warehouseField = (
    <CustomSelect
      name="uuid_destination_warehouse"
      label="Depósito solicitado"
      value={selectedOrder.uuid_destination_warehouse || ''}
      onChange={onSelectChange}
      required
      fullWidth
      size="medium"
      disabled={warehousesLoading}
    >
      {warehousesLoading ? (
        <MenuItem value="" disabled>
          Cargando depósitos...
        </MenuItem>
      ) : null}
      {warehouses.map((warehouse) => (
        <MenuItem key={warehouse.uuid} value={warehouse.uuid}>
          {warehouse.name || warehouse.code}
        </MenuItem>
      ))}
    </CustomSelect>
  );

  const projectsInitialLoading = projectsLoading && projectOptions.length === 0;

  const projectField = (
    <CustomAutoComplete
      key={`project-select-${projectOptions.length}`}
      label="Proyecto"
      name="uuid_project"
      value={selectedOrder.uuid_project || ''}
      onChange={onSelectChange}
      options={projectOptions}
      optionValueKey="value"
      optionLabelKey="label"
      placeholder={
        projectsInitialLoading ? 'Cargando proyectos...' : 'Buscar por barrio, manzana o casa'
      }
      loading={projectsInitialLoading}
      noOptionsText={
        projectsInitialLoading ? 'Cargando proyectos...' : 'No se encontraron proyectos'
      }
      componentsProps={{
        popper: {
          sx: { zIndex: 1400 },
        },
      }}
      required
      size="medium"
    />
  );

  const dateTimeFields = (
    <>
      <CustomTextField
        label="Fecha de necesidad"
        name="needed_date"
        type="date"
        value={selectedOrder.needed_date || ''}
        onChange={onInputChange}
        required
        size="medium"
        InputLabelProps={{ shrink: true }}
      />
      <CustomTextField
        label="Hora de necesidad"
        name="needed_time"
        type="time"
        value={selectedOrder.needed_time || ''}
        onChange={onInputChange}
        required
        size="medium"
        InputLabelProps={{ shrink: true }}
      />
    </>
  );

  const observationsField = (
    <CustomTextArea
      name="observations"
      label="Observaciones"
      value={selectedOrder.observations || ''}
      onChange={onInputChange}
      fullWidth
    />
  );

  const materialsSection = (
    <InternalOrderMaterialsSection
      materials={selectedOrder.materials || []}
      searchEnabled={searchEnabled}
      onAddMaterial={onAddMaterial}
      onUpdateMaterialQuantity={onUpdateMaterialQuantity}
      onRemoveMaterial={onRemoveMaterial}
    />
  );

  if (isMobile) {
    return (
      <Box>
        {sectionTitle}
        <VerticalForm withBorder={false}>
          {warehouseField}
          {projectField}
          {dateTimeFields}
          {observationsField}
        </VerticalForm>
        {materialsSection}
      </Box>
    );
  }

  return (
    <Box>
      {sectionTitle}

      <HorizontalForm withBorder={false} sx={{ p: 1 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, mr: 4 }}>
          {warehouseField}
          {projectField}
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {dateTimeFields}
        </Box>
      </HorizontalForm>

      <Box px={1}>{observationsField}</Box>
      <Box px={1}>{materialsSection}</Box>
    </Box>
  );
};

export default InternalOrderFormSection;
